import { Controller } from "@hotwired/stimulus";

const PAGE_SIZE = 20;

export default class extends Controller {
  static targets = ["section"];

  initialize() {
    this.saved = {};
  }

  sectionTargetConnected(section) {
    const saved = this.saved[section.dataset.section] || { page: 0, sellers: [], details: [] };
    delete this.saved[section.dataset.section];
    this.paginate(section, saved.page);
    saved.sellers.forEach((key) => {
      const row = section.querySelector(`tr[data-seller="${key}"]`);
      if (row && !row.hidden) this.expand(row, true);
    });
    saved.details.forEach(({ id, html }) => {
      const row = section.querySelector(`tr[data-detail="${id}"]`);
      if (!row || row.hidden || html.includes("data-waiting")) return;

      const detail = this.detailOf(row);
      detail.querySelector(".parts-detail__content").innerHTML = html;
      detail.dataset.loaded = "true";
      this.open(row, true);
    });
  }

  sectionTargetDisconnected(section) {
    this.saved[section.dataset.section] = {
      page: Number(section.dataset.page || 0),
      sellers: Array.from(section.querySelectorAll("tr[data-seller].parts-row--open")).map((row) => row.dataset.seller),
      details: Array.from(section.querySelectorAll("tr[data-detail].parts-row--open")).map((row) => ({
        id: row.dataset.detail,
        html: this.detailOf(row).querySelector(".parts-detail__content").innerHTML
      }))
    };
  }

  toggle(event) {
    const row = event.currentTarget;
    this.expand(row, !row.classList.contains("parts-row--open"));
  }

  expand(row, expanded) {
    row.classList.toggle("parts-row--open", expanded);
    row.querySelector("[aria-expanded]").setAttribute("aria-expanded", expanded);
    this.piecesOf(row).forEach((piece) => {
      piece.hidden = !expanded;
      if (!expanded && piece.dataset.detail) this.open(piece, false);
    });
  }

  open(row, expanded) {
    row.classList.toggle("parts-row--open", expanded);
    row.querySelector("[aria-expanded]").setAttribute("aria-expanded", expanded);
    this.detailOf(row).hidden = !expanded;
  }

  reveal(event) {
    event.preventDefault();
    const row = document.getElementById(event.currentTarget.getAttribute("href").slice(1));
    if (!row) return;

    const section = row.closest("tbody");
    this.paginate(section, Math.floor(this.sellersOf(section).indexOf(row) / PAGE_SIZE));
    this.expand(row, true);
    row.scrollIntoView({ block: "center" });
  }

  previous(event) {
    this.turn(event, -1);
  }

  next(event) {
    this.turn(event, 1);
  }

  turn(event, step) {
    const section = event.currentTarget.closest("tbody");
    this.paginate(section, Number(section.dataset.page || 0) + step);
  }

  paginate(section, page) {
    const sellers = this.sellersOf(section);
    const pages = Math.max(Math.ceil(sellers.length / PAGE_SIZE), 1);
    const current = Math.min(Math.max(page, 0), pages - 1);
    section.dataset.page = current;
    sellers.forEach((row, index) => {
      const onPage = Math.floor(index / PAGE_SIZE) === current;
      if (!onPage) this.expand(row, false);
      row.hidden = !onPage;
    });

    const pager = section.querySelector(".parts-table__pager");
    if (!pager) return;

    const [previous, next] = pager.querySelectorAll("button");
    pager.hidden = pages === 1;
    previous.disabled = current === 0;
    next.disabled = current === pages - 1;
    pager.querySelector(".parts-table__position").textContent = `${current + 1} / ${pages}`;
  }

  sellersOf(section) {
    return Array.from(section.querySelectorAll("tr[data-seller]"));
  }

  piecesOf(row) {
    return row.parentElement.querySelectorAll(`tr[data-piece-of="${row.dataset.seller}"]`);
  }

  detailOf(row) {
    return row.parentElement.querySelector(`[id="${row.dataset.detail}-row"]`);
  }
}
